// ──────────────────────────────────────────────
// classifyStorageError — turn opaque IndexedDB/storage failures into
// distinguishable, root-causable telemetry.
//
// Browser-thrown IndexedDB errors are `DOMException`s created deep inside the
// engine (e.g. `request.error`). They carry a `name` (`QuotaExceededError`,
// `UnknownError`, …) but usually NO JavaScript stack — so Sentry records them
// as "No stacktrace available" and, because every failure mode shares the same
// generic message ("Internal error."), aggregates wildly different root causes
// (full disk, corrupted DB, partitioned storage, …) under one issue.
//
// This module fixes both problems:
//   1. `classifyStorageError` maps the raw error to a stable `StorageFailureMode`
//      so Sentry can split/fingerprint issues by root cause.
//   2. `StorageInitError` wraps the raw error in a real `Error` captured at the
//      call site — giving Sentry a genuine stack trace — while preserving the
//      original name/message (via `cause`) for classification.
// ──────────────────────────────────────────────

/** Distinct storage failure modes. Surfaced to Sentry as the `storage_error_type` tag. */
export type StorageFailureMode =
  | 'quota_exceeded' // disk / origin quota is full
  | 'corrupted_db' // on-disk database is damaged or a txn aborted unexpectedly
  | 'blocked_db' // connection blocked/closing (another tab upgrading, teardown race)
  | 'access_denied' // storage blocked — third-party partitioning, privacy mode, disabled cookies
  | 'version_conflict' // requested version is older than the existing on-disk version
  | 'internal_error' // browser-level `UnknownError` — typically underlying I/O failure
  | 'unknown'; // nothing matched — genuinely unclassified

/** Extract a DOMException/Error `name`, or '' for values that have none. */
function errorName(error: unknown): string {
  if (error instanceof DOMException || error instanceof Error) return error.name;
  return '';
}

/**
 * Maps a raw storage error to a {@link StorageFailureMode}.
 *
 * Primary signal is the `DOMException.name`, which is stable across browsers.
 * Falls back to a message heuristic for wrapped/stringified errors that have
 * lost their original type.
 */
export function classifyStorageError(error: unknown): StorageFailureMode {
  // A previously-wrapped error already knows its mode — don't reclassify.
  if (error instanceof StorageInitError) return error.failureMode;

  switch (errorName(error)) {
    case 'QuotaExceededError':
      return 'quota_exceeded';
    case 'VersionError':
      return 'version_conflict';
    case 'AbortError':
    case 'ConstraintError':
    case 'DataError':
      return 'corrupted_db';
    case 'InvalidStateError':
    case 'TransactionInactiveError':
      return 'blocked_db';
    case 'SecurityError':
    case 'NotAllowedError':
      return 'access_denied';
    case 'UnknownError':
      return 'internal_error';
  }

  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
  if (message.includes('quota')) return 'quota_exceeded';
  if (
    message.includes('security') ||
    message.includes('not allowed') ||
    message.includes('partition') ||
    message.includes('blocked')
  ) {
    return 'access_denied';
  }
  if (message.includes('corrupt') || message.includes('abort')) return 'corrupted_db';
  if (message.includes('version')) return 'version_conflict';

  return 'unknown';
}

/**
 * An `Error` raised when storage initialization fails.
 *
 * Constructed at the failure site so it carries a real JS stack trace (unlike
 * the browser's bare `DOMException`), while preserving the original error as
 * `cause` and exposing its classified {@link failureMode} and `originalName`
 * for tagging.
 */
export class StorageInitError extends Error {
  /** Classified root cause, used as the `storage_error_type` Sentry tag. */
  readonly failureMode: StorageFailureMode;
  /** Original DOMException/Error name, e.g. "UnknownError", "QuotaExceededError". */
  readonly originalName: string;

  constructor(operation: string, cause: unknown) {
    const failureMode = classifyStorageError(cause);
    const originalName = errorName(cause) || 'Error';
    const originalMessage = cause instanceof Error ? cause.message : String(cause);

    super(`Storage ${operation} failed [${failureMode}]: ${originalName}: ${originalMessage}`, {
      cause,
    });

    this.name = 'StorageInitError';
    this.failureMode = failureMode;
    this.originalName = originalName;

    // Preserve the prototype chain so `instanceof StorageInitError` holds even
    // when the class is down-levelled by the transpiler.
    Object.setPrototypeOf(this, StorageInitError.prototype);
  }
}

/**
 * Builds the Sentry tag set describing a storage error's root cause, so init
 * failures split by failure mode instead of aggregating under one bucket.
 */
export function storageErrorTags(error: unknown): {
  storage_error_type: StorageFailureMode;
  storage_error_name: string;
} {
  return {
    storage_error_type: classifyStorageError(error),
    storage_error_name:
      error instanceof StorageInitError ? error.originalName : errorName(error) || 'unknown',
  };
}
